import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Role, User, page } from '@cloudsign/shared';
import { z } from 'zod';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { useListState } from '../../lib/useList';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { Badge, Button, Card, EmptyRow, FilterBar, PageHeader, Pager, SelectField, SortHeader, Spinner, TableWrap, TextField, Th } from '../../components/ui';

const UserPage = page(User);
const RoleList = z.array(Role);
const PAGE_SIZE = 20;

export function statusTone(status: string): 'good' | 'warn' | 'bad' | 'neutral' {
  if (status === 'active') return 'good';
  if (status === 'invited') return 'warn';
  if (status === 'disabled') return 'bad';
  return 'neutral';
}

export function useRoles() {
  return useQuery({ queryKey: ['roles'], queryFn: () => api.get('/roles', { schema: RoleList }) });
}

function InviteForm({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const roles = useRoles();
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [roleId, setRoleId] = useState('');
  const create = useMutation({
    mutationFn: () => api.post('/users', { body: { email, fullName, roleId: roleId || roles.data?.[0]?.id, siteIds: [] }, schema: User }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['users'] });
      onDone();
    },
  });
  const errs = fieldErrors(create.error);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };
  return (
    <Card title={t('users.invite')} className="mb-6">
      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <ErrorLine error={create.error} />
        </div>
        <TextField label={t('auth.email')} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} error={errs['email']} />
        <TextField label={t('auth.fullName')} required value={fullName} onChange={(e) => setFullName(e.target.value)} error={errs['fullName']} />
        <SelectField label={t('users.role')} value={roleId || roles.data?.[0]?.id || ''} onChange={(e) => setRoleId(e.target.value)} error={errs['roleId']}>
          {(roles.data ?? []).map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </SelectField>
        <div className="flex items-end gap-2">
          <Button type="submit" variant="primary" disabled={create.isPending || !roles.data}>
            {t('users.sendInvite')}
          </Button>
          <Button onClick={onDone}>{t('common.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}

export function UsersPage() {
  const list = useListState({ sort: 'fullName' });
  const canManage = useCan('users.manage');
  const [status, setStatus] = useState('');
  const [roleId, setRoleId] = useState('');
  const [inviting, setInviting] = useState(false);
  const roles = useRoles();
  const q = useQuery({
    queryKey: ['users', { page: list.page, search: list.search, sort: list.sort, order: list.order, status, roleId }],
    queryFn: () =>
      api.get('/users', {
        query: { page: list.page, pageSize: PAGE_SIZE, search: list.search, sort: list.sort, order: list.order, status, roleId },
        schema: UserPage,
      }),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <PageHeader
        title={t('nav.users')}
        description={t('nav.users.desc')}
        actions={
          canManage ? (
            <Button variant="primary" onClick={() => setInviting(true)} disabled={inviting}>
              {t('users.invite')}
            </Button>
          ) : null
        }
      />
      {inviting ? <InviteForm onDone={() => setInviting(false)} /> : null}
      <FilterBar>
        <TextField label={t('common.search')} type="search" value={list.search} onChange={(e) => list.setSearch(e.target.value)} />
        <SelectField
          label={t('common.status')}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            list.resetPage();
          }}
        >
          <option value="">{t('common.all')}</option>
          {(['active', 'invited', 'disabled'] as const).map((s) => (
            <option key={s} value={s}>
              {t(`status.${s}`)}
            </option>
          ))}
        </SelectField>
        <SelectField
          label={t('users.role')}
          value={roleId}
          onChange={(e) => {
            setRoleId(e.target.value);
            list.resetPage();
          }}
        >
          <option value="">{t('common.all')}</option>
          {(roles.data ?? []).map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </SelectField>
      </FilterBar>
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <TableWrap caption={t('nav.users')}>
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <SortHeader label={t('common.name')} field="fullName" sort={list.sort} order={list.order} onSort={list.toggleSort} />
                <SortHeader label={t('auth.email')} field="email" sort={list.sort} order={list.order} onSort={list.toggleSort} />
                <Th>{t('users.role')}</Th>
                <Th>{t('common.status')}</Th>
                <Th>{t('users.twoFactor')}</Th>
                <SortHeader label={t('users.lastLogin')} field="lastLoginAt" sort={list.sort} order={list.order} onSort={list.toggleSort} />
              </tr>
            </thead>
            <tbody>
              {q.data.items.length === 0 ? (
                <EmptyRow
                  cols={6}
                  action={
                    canManage ? (
                      <Button variant="primary" onClick={() => setInviting(true)}>
                        {t('users.invite')}
                      </Button>
                    ) : null
                  }
                />
              ) : null}
              {q.data.items.map((u) => (
                <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <Link className="font-medium text-brand hover:underline" to={`/admin/users/${u.id}`}>
                      {u.fullName}
                    </Link>
                  </td>
                  <td className="px-4 py-3">{u.email}</td>
                  <td className="px-4 py-3">{u.roleName}</td>
                  <td className="px-4 py-3">
                    <Badge tone={statusTone(u.status)}>{t(`status.${u.status}`)}</Badge>
                  </td>
                  <td className="px-4 py-3">{u.twoFactorEnabled ? t('common.yes') : t('common.no')}</td>
                  <td className="px-4 py-3">{u.lastLoginAt ? formatDateTime(u.lastLoginAt) : t('users.never')}</td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
          <Pager page={q.data.page} pageSize={q.data.pageSize} total={q.data.total} onPage={list.setPage} />
        </>
      )}
    </>
  );
}
